import { DateTime } from 'luxon';
import { areArraysEqual } from 'ontraccr-common/lib/Common';

import sortByString from '../helpers/helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

import Permissions from '../auth/Permissions';

export default {};

export const timestampsToText = ({
  startTime,
  endTime,
  timezone = DateTime.local().zoneName,
}) => {
  const startDT = startTime ? DateTime.fromMillis(startTime, { zone: timezone }) : null;
  const endDT = endTime ? DateTime.fromMillis(endTime, { zone: timezone }) : null;
  if (!startDT || !endDT) return '';
  const endOfEndDay = endDT.endOf('day');
  const isAllDay = endDT.equals(endOfEndDay);
  const isMultiDay = !endDT.hasSame(startDT, 'day');
  if (isMultiDay) {
    const format = isAllDay ? DateTime.DATE_MED : DateTime.DATETIME_MED;
    return `${startDT.toLocaleString(format)} -> ${endDT.toLocaleString(format)}`;
  }
  if (isAllDay) return startDT.toLocaleString(DateTime.DATE_MED);
  return `${startDT.toLocaleString(DateTime.DATE_MED)} ${startDT.toLocaleString(DateTime.TIME_SIMPLE)} -> ${endDT.toLocaleString(DateTime.TIME_SIMPLE)}`;
};

const DEFAULT_BOARD_CARD_SORT = { type: 'default', field: null };

export const getBoardCardSortStorageKey = (boardId) => `boardCardSort-${boardId}`;

export const getStoredBoardCardSort = (boardId) => {
  try {
    const boardCardSortKey = getBoardCardSortStorageKey(boardId);
    const raw = window.localStorage.getItem(boardCardSortKey);
    return JSON.parse(raw) ?? DEFAULT_BOARD_CARD_SORT;
  } catch (err) {
    return DEFAULT_BOARD_CARD_SORT;
  }
};

export const setStoredBoardCardSort = (boardId, { type, field } = {}) => {
  if (!boardId || !type) return;
  window.localStorage.setItem(
    getBoardCardSortStorageKey(boardId),
    JSON.stringify({
      type,
      field: field ?? null,
    }),
  );
};

export const parseDateField = ({ date, time, timezone = DateTime.local().zoneName }) => {
  let timeString = '';
  if (date && !Number.isNaN(date)) {
    const dt = DateTime.fromMillis(date, { zone: timezone });
    timeString = dt.toLocaleString(DateTime.DATE_MED);
  }
  if (time && !Number.isNaN(time)) {
    const prefix = timeString.length > 0 ? ' ' : '';
    const tt = DateTime.fromMillis(time, { zone: timezone });
    timeString += `${prefix}${tt.toLocaleString(DateTime.TIME_SIMPLE)}`;
  }
  return timeString;
};

const sortByDateTimeField = (aDateTime, bDateTime) => {
  const {
    time: aTime = 0,
    timezone: aTimezone = DateTime.local().zoneName,
  } = aDateTime ?? {};
  const {
    time: bTime = 0,
    timezone: bTimezone = DateTime.local().zoneName,
  } = bDateTime ?? {};
  let {
    date: aDate,
  } = aDateTime ?? {};
  let {
    date: bDate,
  } = bDateTime ?? {};

  // removing date explicitly sets its value to null and defaulting doesnt happen
  aDate = aDate ?? 0;
  bDate = bDate ?? 0;

  // Get the iso date of both and compare in one timezone.
  // Otherwise anything on the same day submitted from an earlier timezone would be sorted
  // before other tasks from a later timezone, even if their times were earlier
  const aISO = DateTime.fromMillis(aDate, { zone: aTimezone }).toISODate();
  const bISO = DateTime.fromMillis(bDate, { zone: bTimezone }).toISODate();

  const aDay = DateTime.fromISO(aISO).startOf('day').valueOf();
  const bDay = DateTime.fromISO(bISO).startOf('day').valueOf();

  // first compare the day
  if (aDay < bDay) return -1;
  if (aDay > bDay) return 1;

  // compare time if day is the same
  if (aTime < bTime) return -1;
  if (aTime > bTime) return 1;

  return 0;
};

const getLastMoveTimestamp = ({ lastMoveTimestamp, createdAt }) => lastMoveTimestamp ?? createdAt;

const sortCardArrayByKey = (cardArray, sortType, cardDateTimeMap) => {
  const [key, dir] = sortType.split('-');
  const mod = dir === 'asc' ? 1 : -1;
  cardArray.sort((a, b) => {
    switch (key) {
      case 'title':
        return sortByString(key)(a, b) * mod;
      case 'number':
        return (a.number - b.number) * mod;
      case 'lastMoveTimestamp':
        return (getLastMoveTimestamp(b) - getLastMoveTimestamp(a)) * mod;
      case 'dateTime':
        return sortByDateTimeField(cardDateTimeMap[a.id], cardDateTimeMap[b.id]) * mod;
      default:
        return a.orderIndex - b.orderIndex;
    }
  });
};

export const sortAndFilterCards = ({
  cards,
  sortType,
  filters,
  field,
  projects = [],
  board,
  filteredCardIds = null,
}) => {
  const {
    users: filterUsers,
    link: filterLink,
    alwaysShowAssigned: filterAlwaysShowAssigned,
  } = filters ?? {};

  const backendIdSet = filteredCardIds ? new Set(filteredCardIds) : null;
  const filterUserSet = filterUsers?.length ? new Set(filterUsers) : null;

  const {
    linkType: filterLinkType,
    linkId: filterLinkId,
  } = filterLink ?? {};
  const isCustomerLink = filterLinkType === 'customerId' && filterLinkId;
  const projectSet = new Set();
  if (isCustomerLink) {
    projects.forEach((project) => {
      if (project.customerId === filterLinkId) {
        projectSet.add(project.id);
      }
    });
  }

  const cardDateTimeMap = {};
  const cardArray = [];
  const shouldSearchDateTime = sortType.startsWith('dateTime') && field;

  if (shouldSearchDateTime) {
    cards.forEach((card) => {
      const { id: cardId, data: sections = [] } = card;
      const flatFields = sections.flatMap((section) => section.fields);
      flatFields.forEach((formField) => {
        if (formField?.fieldId === field) {
          cardDateTimeMap[cardId] = formField?.response ?? {};
        }
      });
    });
  }

  cards.forEach((card) => {
    const {
      users, link, id: cardId,
    } = card;
    const shouldShowAssigned = (
      filterAlwaysShowAssigned
      && users.includes(Permissions.id)
    );
    if (shouldShowAssigned) {
      cardArray.push(card);
      return;
    }
    if (backendIdSet && !backendIdSet.has(cardId)) return;

    if (filterUserSet && !users.some((userId) => filterUserSet.has(userId))) return;
    if (
      filterLinkType
      && filterLinkId
    ) {
      let linkMatches = (
        link?.linkType === filterLinkType
        && link?.linkId === filterLinkId
      );
      if (isCustomerLink && link?.linkType === 'projectId') {
        linkMatches = projectSet.has(link.linkId);
      }
      if (!linkMatches) return;
    }
    cardArray.push(card);
  });
  if (board?.sortType === 'default') sortCardArrayByKey(cardArray, sortType, cardDateTimeMap);
  return cardArray;
};

export const getTemplateMap = (cardTemplate = {}) => {
  const tMap = {};
  const { fields: sections = [] } = cardTemplate;
  sections.forEach((section) => {
    const { fields = [], id: sectionId } = section;
    if (!(sectionId in tMap)) tMap[sectionId] = {};
    fields.forEach((field) => {
      tMap[sectionId][field.id] = field;
    });
  });
  return tMap;
};

export const parseYesNo = (response = {}) => {
  const { value, explanation } = response;
  let ans = null;
  // value === undefined if user didnt fill out the field
  if (value === true) {
    ans = 'Yes';
  } else if (value === false) {
    ans = 'No';
  }
  if (explanation) ans += `. ${explanation}`;
  return ans;
};

export const parseDropdown = ({ values = [] } = {}, config = {}) => {
  const {
    dataType,
    settings = {},
    projectIdMap = {},
  } = config;
  return (
    values.reduce((acc, val) => {
      let label = val?.name;
      if (dataType === 'Projects') {
        const {
          [val.id]: {
            name,
            number,
          } = {},
        } = projectIdMap;
        label = formatProjectLabelFromCompanySettings({
          name,
          number,
          settings,
        });
      }
      if (label) return `${acc}${acc.length > 0 ? ', ' : ''}${label}`;
      return acc;
    }, '')
  );
};

export const getLastMove = (lastMoveTimestamp) => (lastMoveTimestamp ? DateTime.fromMillis(lastMoveTimestamp) : DateTime.local());

export const getUnreadCommCount = ({
  link,
  unreadClientComms = [],
  projectIdMap = {},
}) => {
  if (!link) return 0;
  let customerId;
  const { linkType, linkId } = link;
  if (linkType === 'customerId') customerId = linkId;
  if (linkType === 'projectId') {
    const { [linkId]: { customerId: projectCustomer } = {} } = projectIdMap;
    customerId = projectCustomer;
  }
  if (!customerId) return 0;
  return unreadClientComms.filter((note) => note.customerId === customerId).length;
};

export const handleCardMove = ({
  disableCrossColumnSorting,
  cardsByStatusId = {},
  dragEvent: {
    destination,
    source,
    type,
    draggableId,
  } = {},
  fullCards,
}) => {
  if (!destination) return {};
  const {
    droppableId: destinationId,
    index: destinationIndex,
  } = destination;
  const {
    droppableId: sourceId,
    index: sourceIndex,
  } = source;
  if (
    destinationId === sourceId
    && destinationIndex === sourceIndex
  ) return {};
  if (type === 'CARDS') {
    const {
      [destinationId]: destinationCards = [],
    } = cardsByStatusId;
    let destIdx = destinationIndex;

    const highestIndex = destinationCards?.reduce((acc,cardId) => (
      Math.max(acc, fullCards[cardId]?.orderIndex ?? -1)
    ), -1) ?? -1;

    if (disableCrossColumnSorting && sourceId !== destinationId) {
      destIdx = highestIndex + 1;
    } else if (destinationIndex > (highestIndex + 1)) {
      // Dropped into shadow
      destIdx = highestIndex + 1;
    }

    return {
      draggableId,
      destinationId,
      destinationIndex: destIdx,
      sourceId,
      sourceIndex,
    };
  }
  return {};
};

export const parseCustomResponses = ({
  field = {},
  projectIdMap,
  settings = {},
}) => {
  let displayData = '';
  const {
    selectedType,
    type,
    response = {},
    configProps: {
      dataType,
    } = {},
  } = field;
  const actualType = selectedType ?? type;
  switch (actualType) {
    case 'text': {
      displayData = response.value ?? '';
      break;
    }
    case 'dateRange': {
      displayData = timestampsToText(response);
      break;
    }
    case 'dateTime': {
      displayData = parseDateField(response);
      break;
    }
    case 'dropdown': {
      const config = {
        dataType,
        projectIdMap,
        settings,
      };
      displayData = parseDropdown(response, config);
      break;
    }
    case 'yes-no': {
      displayData = parseYesNo(response);
      break;
    }
    default:
      displayData = response?.value ?? '';
      break;
  }
  return displayData;
};

export const getGanttZoomScales = ({
  max,
}) => {
  const numbers = [];
  for (let i = 0.25; i <= max; i += 0.25) {
    numbers.push(i);
  }

  return numbers.map((num) => ({
    value: num,
    label: `${num * 100}%`,
  }));
};

export const CARD_AUTOSAVE_INTERVAL = 10000; // 10 seconds
export const CARD_CREATE_STORAGE_KEY = 'cardCreateData';
export const CARD_EDIT_STORAGE_KEY = 'cardEditData';

const CARD_BASE_KEYS = [
  'title',
  'color',
  'statusId',
  'users',
  'link',
  'dateRange',
];

export const cardBaseValuesAreDifferent = ({
  initialCard = {},
  currentCard = {},
}) => (
  CARD_BASE_KEYS.some((key) => {
    const initialValue = initialCard[key];
    const currentValue = currentCard[key];

    switch (key) {
      case 'link':
        return (
          initialValue?.linkType !== currentValue?.linkType
          || initialValue?.linkId !== currentValue?.linkId
        );
      case 'users':
        return !areArraysEqual(initialValue, currentValue);
      case 'dateRange':
        return (
          initialValue?.startDate !== currentValue?.startDate
          || initialValue?.endDate !== currentValue?.endDate
        );
      default:
        return initialValue !== currentValue;
    }
  })
);

/**
 * Resolve custom dropdown options for a dynamic attribute from the profile's custom field template.
 * Dynamic attributes on board cards display a value from another profile's custom fields
 * (projects, customers, equipment).
 *
 * @param {string} fieldDataType - Data type of the underlying dynamic attribute field; options are
 *   only resolved when this is 'Custom'.
 * @param {string} dataType - Which profile the dynamic attribute points at:
 *   'projects' | 'customers' | 'equipment' | 'users'.
 * @param {string} fieldId - Id of the custom field in that profile's template.
 * @param {string|null} subType - For equipment, the equipment type id (null = default).
 * @param {Object} projectCustomFieldTemplate - Project custom field templates.
 * @param {Object} customerCustomFieldTemplate - Customer custom field templates.
 * @param {Array} equipmentTypes - Equipment types with custom fields.
 * @returns {Array<{ id: string, name: string }>} Custom field options for the dropdown
 */
export const getDynamicAttributeCustomOptions = ({
  fieldDataType,
  dataType,
  fieldId,
  subType,
  projectCustomFieldTemplate,
  customerCustomFieldTemplate,
  userCustomFieldTemplate,
  equipmentTypes,
}) => {
  if (fieldDataType !== 'Custom') return [];

  const customField = getDynamicAttributeTargetField({
    dataType,
    fieldId,
    subType,
    projectCustomFieldTemplate,
    customerCustomFieldTemplate,
    userCustomFieldTemplate,
    equipmentTypes,
  });
  if (!customField?.configProps) return [];

  const { customOptions = [] } = customField.configProps;
  return customOptions.map(({ id, name }) => ({
    id,
    name,
  }));
};

/**
 * Resolve subDataType (e.g. bucket template id) from the underlying profile custom field.
 */
export const getDynamicAttributeSubDataType = ({
  dataType,
  fieldId,
  subType,
  projectCustomFieldTemplate,
  customerCustomFieldTemplate,
  userCustomFieldTemplate,
  equipmentTypes,
}) => (
  getDynamicAttributeTargetField({
    dataType,
    fieldId,
    subType,
    projectCustomFieldTemplate,
    customerCustomFieldTemplate,
    userCustomFieldTemplate,
    equipmentTypes,
  })?.configProps?.subDataType
);

const getDynamicAttributeTargetField = ({
  dataType,
  fieldId,
  subType,
  projectCustomFieldTemplate,
  customerCustomFieldTemplate,
  userCustomFieldTemplate,
  equipmentTypes,
}) => {
  let fields = [];
  if (dataType === 'projects' && projectCustomFieldTemplate?.fields) {
    fields = projectCustomFieldTemplate?.fields ?? [];
  } else if (dataType === 'customers' && customerCustomFieldTemplate?.fields) {
    fields = customerCustomFieldTemplate?.fields ?? [];
  } else if (dataType === 'users' && userCustomFieldTemplate?.fields) {
    fields = userCustomFieldTemplate?.fields ?? [];
  } else if (dataType === 'equipment' && equipmentTypes?.length) {
    const equipmentType = equipmentTypes.find(
      (type) => type.id === subType || (subType === null && type.isDefault),
    );
    fields = equipmentType?.customFields?.fields ?? [];
  }

  return fields.find((f) => f.id === fieldId);
};

export const getBoardColorLabel = (cardColor, boardColors = []) => {
  if (!cardColor || !boardColors?.length) return null;
  const normalizedColor = cardColor.toUpperCase();
  const match = boardColors.find(
    (entry) => entry.color?.toUpperCase() === normalizedColor,
  );
  return match?.label ?? null;
};

export const getColorsForBoard = (boardId, {
  selectedBoard,
  boardDetailsMap = {},
  boardColors = [],
} = {}) => {
  if (!boardId) return [];

  const fromSelected = selectedBoard?.id === boardId
    ? selectedBoard?.colors
    : null;
  if (fromSelected?.length) return fromSelected;

  const fromDetails = boardDetailsMap[boardId]?.colors;
  if (fromDetails?.length) return fromDetails;

  return boardColors.filter((entry) => entry.boardId === boardId);
};
